import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { isAdminLike, isUserManager, hasPerm, hasAnyView } from "@/lib/roles";
import { tenantSlugFromPath } from "@/lib/tenant";
import { SubdomainRedirect } from "@/components/SubdomainRedirect";
import { TenantBrandingSync } from "@/components/TenantBrandingSync";
import { LicenseGate } from "@/components/LicenseGate";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import NoAccessScreen from "@/pages/NoAccessScreen";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Bairros from "@/pages/Bairros";
import Rotas from "@/pages/Rotas";
import ResetPassword from "@/pages/ResetPassword";
import Logistica from "@/pages/Logistica";
import MonitoramentoVivo from "@/pages/MonitoramentoVivo";
import Layout from "@/pages/Layout";
import Dashboard from "@/pages/Dashboard";
import Entregas from "@/pages/Entregas";
import CrudPage from "@/pages/CrudPage";
import Usuarios from "@/pages/Usuarios";
import Relatorios from "@/pages/Relatorios";
import Backup from "@/pages/Backup";
import Historico from "@/pages/Historico";
import Empresa from "@/pages/Empresa";
import EmpresaConfig from "@/pages/EmpresaConfig";
import MobileAppAdmin from "@/pages/MobileAppAdmin";
import Auditoria from "@/pages/Auditoria";
import Acerto from "@/pages/Acerto";
import WhatsAppLogs from "@/pages/WhatsAppLogs";
import DataRepair from "@/pages/DataRepair";
import MobileLogs from "@/pages/MobileLogs";
import BackupVault from "@/pages/BackupVault";
import MobileEntregador from "@/pages/MobileEntregador";
import Kanban from "@/pages/Kanban";
import TwoFactorSettings from "@/pages/TwoFactorSettings";
import TrackingPublic from "@/pages/TrackingPublic";
import Termos from "@/pages/Termos";
import "@/App.css";

// Determina onde mandar um usuário AUTENTICADO acessando "/" raiz:
// - entregador → /mobile
// - super admin → /admin/empresas (Painel Global)
// - tenant user com 1 empresa (com slug) → /{slug}/
// - tenant user com múltiplas empresas → /login (força passar pelo picker)
function isSuperAdminUser(u) {
  // Regra estrita: role compatível E empresa_ids vazio E não está impersonando.
  // Alinhada com backend `is_superadmin()`. Se um usuário tem role="super_admin"
  // mas empresa_ids=[X] (estado inconsistente), NÃO é considerado super admin.
  if (!u || u._impersonating) return false;
  const roleCompatible = u.role === "super_admin" || u.role === "admin";
  const noEmpresa = !u.empresa_ids || u.empresa_ids.length === 0;
  return roleCompatible && noEmpresa;
}

function RootRedirect() {
  const { user, empresas, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  if (!user) return <Landing />;
  if (user.entregador_id || user.role === "entregador") return <Navigate to="/mobile" replace />;
  if (isSuperAdminUser(user)) return <Navigate to="/admin/empresas" replace />;
  // Conta sem permissões: mostra tela dedicada em vez de tentar redirecionar pro tenant
  // (evita loop Dashboard nega → volta "/" → volta tenant → sidebar piscando)
  if (!hasAnyView(user)) return <NoAccessScreen />;
  if (empresas?.length === 1 && empresas[0].slug) return <Navigate to={`/${empresas[0].slug}/`} replace />;
  return <Navigate to="/login" replace />;
}

// Retorna a rota de login apropriada preservando o slug (ex: /fazbem/entregas sem auth → /fazbem/login)
function loginPathFor(pathname) {
  const slug = tenantSlugFromPath(pathname);
  return slug ? `/${slug}/login` : "/login";
}

// Verifica se o usuário tem autorização para acessar a rota; retorna null se OK, ou o path de redirect.
// Semântica fev/2026 — matrix-first, sem fallback role para não-SA:
//  · adminOnly       → super_admin OU tem ao menos 1 view=true na matrix
//  · userManagerOnly → super_admin OU users.v=true na matrix
//  · superAdminOnly  → apenas super_admin
//  · perm=<resource> → super_admin OU {resource}.v=true na matrix
function checkAuthorization(user, opts) {
  if (opts.superAdminOnly && !isSuperAdminUser(user)) return opts.fallback;
  if (opts.userManagerOnly) {
    if (!(isSuperAdminUser(user) || hasPerm(user, "users", "v"))) return opts.fallback;
  }
  if (opts.adminOnly) {
    const matrix = user?.perms_matrix;
    const anyView = matrix && typeof matrix === "object" && Object.values(matrix).some((r) => r?.v);
    // _impersonating: SA em Modo Suporte tem acesso total no frontend (backend valida)
    if (!(isSuperAdminUser(user) || user?._impersonating || anyView)) return opts.fallback;
  }
  if (opts.perm && !isSuperAdminUser(user) && !hasPerm(user, opts.perm, "v")) return opts.fallback;
  return null;
}

// Mapa de recursos → primeira rota do sidebar (usado no fallback anti-loop do Protected).
// Ordem = prioridade de "próxima página" quando o Dashboard nega acesso.
const RESOURCE_TO_PATH = {
  dashboard: "",
  entregas: "entregas",
  kanban: "kanban",
  acertos: "acerto",
  entregadores: "entregadores",
  bairros: "bairros",
  rotas: "rotas",
  regioes: "regioes",
  tipos_entrega: "tipos",
  formas_pgto: "formas-pgto",
  users: "usuarios",
  empresas: "configuracao",
  relatorios: "relatorios",
  logistica: "logistica",
  auditoria: "auditoria",
  backup: "backup",
};

// Descobre a primeira rota acessível dentro do tenant para o usuário — usado quando o
// Protected sofre denied e o fallbackPath criaria um loop com a rota atual (bug fev/2026).
function firstAccessibleTenantPath(user, slug) {
  if (!user?.perms_matrix) return `/${slug}/entregas`;  // fallback conservador
  for (const [resource, path] of Object.entries(RESOURCE_TO_PATH)) {
    if (hasPerm(user, resource, "v")) return `/${slug}/${path}`.replace(/\/$/, `/${slug}/`);
  }
  return `/${slug}/entregas`;
}

function Protected({ children, adminOnly = false, userManagerOnly = false, superAdminOnly = false, perm = null, fallbackPath }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  if (!user) return <Navigate to={loginPathFor(location.pathname)} replace />;
  const isEntregador = user.entregador_id || user.role === "entregador";
  if (isEntregador && !location.pathname.startsWith("/mobile")) return <Navigate to="/mobile" replace />;
  const denied = checkAuthorization(user, { adminOnly, userManagerOnly, superAdminOnly, perm, fallback: fallbackPath || "/" });
  if (denied) {
    // Anti-loop (bug fev/2026 "pisca e trava"): se o fallback for a mesma URL atual OU o root
    // que redireciona pra cá de novo, redireciona pra primeira rota permitida dentro do tenant.
    const slug = tenantSlugFromPath(location.pathname);
    const wouldLoop = denied === location.pathname || denied === "/" || denied === `/${slug}/`;
    if (wouldLoop && slug && hasAnyView(user)) {
      return <Navigate to={firstAccessibleTenantPath(user, slug)} replace />;
    }
    return <Navigate to={denied} replace />;
  }
  return children;
}

function ProtectedMobile({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <LicenseGate variant="mobile">{children}</LicenseGate>;
}

// Guarda contra slug inexistente: se /:slug não bater com nenhuma empresa visível ao user,
// e o slug não é uma rota reservada, mostra 404-ish. Também injeta o slug como key no Layout
// para forçar remount ao trocar de tenant.
function TenantScope() {
  const { slug } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const tenantSlug = tenantSlugFromPath(location.pathname);
  const fbPath = `/${slug}/`;
  // Se o path deu match mas o slug caiu na reserved list, deixa router seguir outras rotas
  if (!tenantSlug || tenantSlug !== slug) return <Navigate to="/" replace />;
  // Curto-circuita antes do Layout renderizar pra evitar loop de redirect (bug fev/2026):
  // user sem view=true em matrix não deve entrar em rotas de tenant — mostra NoAccessScreen
  if (user && !user.entregador_id && !isSuperAdminUser(user) && !hasAnyView(user)) {
    return <NoAccessScreen />;
  }
  return (
    <Protected fallbackPath={fbPath}>
      <LicenseGate variant="web">
        <Layout />
      </LicenseGate>
    </Protected>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <SubdomainRedirect />
          <TenantBrandingSync />
          <Routes>
          {/* Rota PÚBLICA de rastreamento (sem auth) — Fase B da Z-API */}
          <Route path="/rastreio/:hash" element={<TrackingPublic />} />

          {/* Rotas top-level (sem slug) */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/termos" element={<Termos />} />
          <Route path="/privacidade" element={<Termos />} />
          <Route path="/lgpd" element={<Termos />} />
          <Route path="/mobile" element={<ProtectedMobile><MobileEntregador /></ProtectedMobile>} />
          <Route path="/entregador" element={<ProtectedMobile><MobileEntregador /></ProtectedMobile>} />

          {/* Painel Global do Super Admin (fora do escopo de tenant) */}
          <Route path="/admin/empresas" element={<Protected superAdminOnly fallbackPath="/login"><Layout /></Protected>}>
            <Route index element={<Empresa />} />
          </Route>
          <Route path="/admin/data-repair" element={<Protected superAdminOnly fallbackPath="/login"><Layout /></Protected>}>
            <Route index element={<DataRepair />} />
          </Route>
          <Route path="/admin/mobile-logs" element={<Protected superAdminOnly fallbackPath="/login"><Layout /></Protected>}>
            <Route index element={<MobileLogs />} />
          </Route>
          <Route path="/admin/backup-vault" element={<Protected superAdminOnly fallbackPath="/login"><Layout /></Protected>}>
            <Route index element={<BackupVault />} />
          </Route>
          <Route path="/admin/security" element={<Protected superAdminOnly fallbackPath="/login"><Layout /></Protected>}>
            <Route index element={<TwoFactorSettings />} />
          </Route>
          <Route path="/admin/mobile-app" element={<Protected superAdminOnly fallbackPath="/login"><Layout /></Protected>}>
            <Route index element={<MobileAppAdmin />} />
          </Route>

          {/* Rotas do tenant (com slug) */}
          <Route path="/:slug/login" element={<Login />} />
          <Route path="/:slug" element={<TenantScope />}>
            <Route index element={<Protected perm="dashboard"><Dashboard /></Protected>} />
            <Route path="entregas" element={<Entregas />} />
            <Route path="kanban" element={<Protected perm="kanban"><Kanban /></Protected>} />
            <Route path="historico" element={<Historico />} />
            <Route path="acerto" element={<Protected perm="acertos"><Acerto /></Protected>} />
            <Route path="entregadores" element={<Protected adminOnly><CrudPage title="Entregadores" path="entregadores" extraFields={[{ key: "telefone", label: "Telefone (com DDD)" }]} enableUserAccount /></Protected>} />
            <Route path="bairros" element={<Protected adminOnly><Bairros /></Protected>} />
            <Route path="rotas" element={<Protected perm="rotas"><Rotas /></Protected>} />
            <Route path="regioes" element={<Protected adminOnly><CrudPage title="Regiões" path="regioes" /></Protected>} />
            <Route path="tipos" element={<Protected adminOnly><CrudPage title="Tipos de Entrega" path="tipos-entrega" /></Protected>} />
            <Route path="formas-pgto" element={<Protected adminOnly><CrudPage title="Formas de Pagamento" path="formas-pgto" /></Protected>} />
            <Route path="usuarios" element={<Protected userManagerOnly><Usuarios /></Protected>} />
            <Route path="relatorios" element={<Relatorios />} />
            <Route path="backup" element={<Backup />} />
            <Route path="auditoria" element={<Protected perm="auditoria"><Auditoria /></Protected>} />
            <Route path="whatsapp-logs" element={<Protected perm="whatsapp_logs"><WhatsAppLogs /></Protected>} />
            <Route path="logistica" element={<Protected perm="logistica"><Logistica /></Protected>} />
            <Route path="monitoramento" element={<Protected perm="monitoramento"><MonitoramentoVivo /></Protected>} />
            <Route path="configuracao" element={<Protected perm="empresas"><EmpresaConfig /></Protected>} />
          </Route>

          {/* Fallback: rota desconhecida → home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}
export default App;
